import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Action } from 'redux';

import lang from '../../../../lib/language';
import { SimplePagedResponse } from '../../../../lib/models/common-response-types';
import { GapperResponse } from '../../../../lib/models/fundamental/types';
import { RootState } from '../../../../lib/store';
import { loadDiscoverChartData } from '../../../../lib/store/common-actions';
import { FundamentalCall, SectorEnum } from '../../../../lib/store/fundamental/types';
import { getLimitedText } from '../../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../../lib/util/error-handling/StatusByCallHelpers';
import { manageSubscriptions } from '../../../../lib/util/TradingHelpers';

import LeftArrowIcon from '../../../../assets/img/icon-circle-arrow-left.svg';
import RightArrowIcon from '../../../../assets/img/icon-circle-arrow-right.svg';
import { ChartLine, ClickableImage } from '../../../../components';
import ClickableDiv from '../../../../components/ClickableDiv/ClickableDiv';
import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import { useIsMobile } from '../../../../hooks';
import { UI_ROUTES } from '../../../../util/routes';
import {
  HOT_GAPPERS_FIRST_PART_SCROLL_POSITION,
  HOT_GAPPERS_NEXT_BTN_ROW_COUNT,
  HOT_GAPPERS_ROW_COUNT,
  HOT_GAPPERS_SYMBOL_NAME_LIMIT,
} from '../../HotGappers.constants';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import styles from '../../HotGappers.module.scss';

type OwnProps = { isGapUpSelected:boolean, data: SimplePagedResponse<GapperResponse>, title: string, }
type Props = OwnProps & ReduxProps;

const HotGappersList = (props: Props) => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const { companyLogoAndName, getChartData, isHotGappersReady, data, isGapUpSelected, title } = props;
  const gapperResponses: GapperResponse[] = data.content;
  const hasMore = gapperResponses.length > HOT_GAPPERS_ROW_COUNT;
  const hasNext = gapperResponses.length > HOT_GAPPERS_NEXT_BTN_ROW_COUNT;
  const scrollContainerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    setScrollPosition(0);
  }, [isGapUpSelected]);

  const scrollLeft = () => {
    setScrollPosition(scrollPosition - HOT_GAPPERS_FIRST_PART_SCROLL_POSITION);
    if (scrollContainerRef.current) {
      // @ts-ignore
      scrollContainerRef.current.scrollBy({ left: -HOT_GAPPERS_FIRST_PART_SCROLL_POSITION, behavior: 'smooth' });
    }
  };

  const hasMoreRight = () => {
    if (gapperResponses.length <= 4) {
      return false;
    }

    if (scrollPosition === 0) {
      return true;
    }

    if (gapperResponses.length <= HOT_GAPPERS_ROW_COUNT) {
      return false;
    }

    if (scrollPosition <= 1400) {
      return true;
    }

    return false;
  };

  const scrollRight = () => {
    const scroll = scrollPosition + HOT_GAPPERS_FIRST_PART_SCROLL_POSITION;
    setScrollPosition(scroll);
    if (scrollContainerRef.current) {
      // @ts-ignore
      scrollContainerRef.current.scrollBy({ left: scroll, behavior: 'smooth' });
    }
  };

  const renderList = gapperResponses.slice(0, HOT_GAPPERS_ROW_COUNT).map((item: GapperResponse) => {
    const logo = companyLogoAndName[item.symbol]?.logo ?? item.logoBase64;
    const url = companyLogoAndName[item.symbol]?.url ?? item.url;
    const displayName = getLimitedText(companyLogoAndName[item.symbol]?.name, HOT_GAPPERS_SYMBOL_NAME_LIMIT);

    return (
      <ClickableDiv
        key={item.symbol}
        onClick={() => history.push(`${UI_ROUTES.symbol}/${item.symbol}`)}
        className={styles.gapperCard}
      >
        <div className={styles.logoAndSymbol}>
          {(url || logo) && (
            <CompanyLogo
              className={styles.logo}
              url={url}
              logo={logo}
              symbol={item.symbol}
            />
          )}
          {' '}
          <p className={styles.symbol}>{item.symbol}</p>
          <p className={styles.displayName}>
            {displayName}
          </p>
        </div>
        <div className={styles.chart}>
          {isMobile && <div className={styles.chartCover} />}
          <ChartLine isChartInCard symbol={item.symbol} />
        </div>
        <div className={styles.price}>
          <PriceAndChange item={item} />
        </div>
      </ClickableDiv>
    );
  });

  useEffect(() => {
    if (isHotGappersReady) {
      const sectorSymbols = gapperResponses?.map(item => item.symbol).join(',');
      getChartData(sectorSymbols);
      manageSubscriptions('discover/hot-gappers');
    }
  }, [gapperResponses, isGapUpSelected, isHotGappersReady]);// eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={styles.listContainer}
    >
      {!isMobile && scrollPosition > (HOT_GAPPERS_FIRST_PART_SCROLL_POSITION - 1) && (
        <ClickableImage
          id="hotGappersPrevBtn"
          src={LeftArrowIcon}
          className={styles.buttonPrevious}
          onClick={scrollLeft}
        />
      )}
      <div
        className={styles.list}
        ref={scrollContainerRef}
      >
        {renderList}
        {hasMore
          && (
            <ClickableDiv
              onClick={() => history.push({
                pathname: `${UI_ROUTES.discover}${UI_ROUTES.popularGroup}/${SectorEnum.Gappers}`,
                state: { group: { id: 10, sector: SectorEnum.Gappers, symbolCount: 0 }, sector: SectorEnum.Gappers },
              })}
              className={styles.gapperCard}
            >
              <div className={styles.logoAndSymbol}>
                <div className={styles.viewAllBtn}>
                  <span>{lang.commonViewAll()}</span>
                </div>
              </div>
            </ClickableDiv>
          )}
      </div>
      {!isMobile && hasNext && hasMoreRight() && (
        <ClickableImage
          id="hotGappersNextBtn"
          src={RightArrowIcon}
          className={styles.buttonNext}
          onClick={scrollRight}
        />
      )}

    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isHotGappersReady: isCallStatusReady(FundamentalCall.getGappers, state.fundamental.statusByCall),
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getChartData: (symbols: string) => dispatch(loadDiscoverChartData(symbols)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(HotGappersList);
